// Central EN/FR translation dictionary
export const translations = {
  en: {
    // Nav
    nav: {
      home: "Home",
      about: "About Us",
      impact: "Impact",
      programs: "Programs",
      leadership: "Leadership",
      membership: "Membership",
      getInvolved: "Get Involved",
      contact: "Contact",
      donate: "Donate",
      join: "Join Now",
    },

    // Common
    common: {
      learnMore: "Learn More",
      joinCommunity: "Join Our Community",
      donateNow: "Donate Now",
      volunteer: "Volunteer",
      partner: "Partner With Us",
      submit: "Submit",
      sending: "Sending...",
      required: "Required",
      thanks: "Thank you!",
      close: "Close",
      readMore: "Read more",
      viewPrograms: "View All Programs",
      comingSoon: "Secure online donation coming soon. Please contact us to contribute today.",
      chooseAmount: "Choose your donation amount",
      customAmount: "Custom amount",
      donateWithCard: "Donate securely with card",
      donating: "Preparing checkout...",
      donationSuccess: "Thank you for your generous donation!",
      donationCancelled: "Donation cancelled. No charge was made.",
      checkingPayment: "Confirming your payment...",
      donorNameOptional: "Your name (optional)",
      donorEmailOptional: "Your email (optional)",
      backHome: "Back to home",
      tryAgain: "Try again",
    },

    // Home
    home: {
      tagline: "Rooted in Compassion · Growing Together",
      heroTitle1: "Empowering Communities.",
      heroTitle2: "Restoring Dignity.",
      heroTitle3: "Building Hope.",
      heroSubtitle:
        "Supporting vulnerable individuals and families across the United States, Africa, and Europe through compassion, unity, and action.",
      impactEyebrow: "Our Focus",
      impactTitle: "Where we show up",
      impactStrip: [
        { title: "Global Reach", desc: "Active across 3 continents." },
        { title: "Orphan Support", desc: "Care for displaced children." },
        { title: "Environmental Care", desc: "Planting roots for the future." },
        { title: "Community Assistance", desc: "Weddings, funerals, welfare." },
      ],
      aboutEyebrow: "Who We Are",
      aboutTitle: "A tree grows strong when the community nurtures its roots.",
      aboutBody:
        "Grand Kasaï Nsanga Lubangu Corporation (GKNL-Inc.) is a non-profit built on the Bantu spirit of solidarity. We stand with vulnerable families, orphans, and communities — offering structured support through bereavement, celebration, and everyday life.",
      programsEyebrow: "Programs",
      programsTitle: "How we help",
      programsItems: [
        {
          title: "Charity Support",
          desc: "Direct aid for individuals and families facing hardship.",
        },
        {
          title: "Orphan Care",
          desc: "Focused support for orphans in refugee camps and communities.",
        },
        {
          title: "Environmental Initiatives",
          desc: "Tree-planting and sustainability projects rooted in local action.",
        },
        {
          title: "Community Assistance",
          desc: "Funeral and wedding support for members and their families.",
        },
      ],
      ctaEyebrow: "Get Involved",
      ctaTitle: "Be Part of the Change",
      ctaSubtitle: "Every hand that lifts, lifts us all. Volunteer, give, or join our growing community.",
    },

    // About
    about: {
      eyebrow: "About Us",
      title: "Grounded in purpose. Branching toward hope.",
      whoTitle: "Who We Are",
      whoBody:
        "GKNL-Inc. is a non-profit corporation uniting people of Kasaï heritage and friends around the world. We honor the legacy of our ancestors by caring for the living — supporting those facing financial, emotional, and social challenges.",
      missionTitle: "Our Mission",
      missionBody:
        "To support individuals and families facing financial and social difficulties through structured programs in charity, bereavement, celebration, and community welfare.",
      visionTitle: "Our Vision",
      visionBody:
        "A connected network of home-care services, daycare centers, and volunteers providing dignity and opportunity to vulnerable communities on three continents.",
      valuesTitle: "Core Values",
      values: [
        { title: "Respect & Dignity", desc: "Every member is treated with honor — regardless of background." },
        { title: "Unity", desc: "We bridge cultures, languages, and generations." },
        { title: "Accountability", desc: "Transparent operations and financial integrity." },
        { title: "Peaceful Resolution", desc: "Conflicts are resolved with wisdom and respect." },
      ],
    },

    // Impact
    impact: {
      eyebrow: "Our Impact",
      title: "Small acts, deep roots.",
      subtitle:
        "Together with our members, donors, and volunteers, we deliver structured help where it matters most.",
      stats: [
        { value: 100, suffix: "+", label: "Families Supported" },
        { value: 3, suffix: "", label: "Continents" },
        { value: 12, suffix: "+", label: "Community Events / Year" },
        { value: 50, suffix: "+", label: "Active Volunteers" },
      ],
      pillars: [
        {
          title: "Humanitarian Support",
          desc: "Emergency aid, household support, and direct cash assistance for vulnerable households.",
        },
        {
          title: "Orphan Support",
          desc: "Focused care and supplies for orphans — with a special commitment to refugee camps.",
        },
        {
          title: "Environmental Initiatives",
          desc: "Tree planting, community clean-ups, and sustainability education.",
        },
        {
          title: "Community Assistance",
          desc: "Structured support for funerals, weddings, and family milestones.",
        },
      ],
    },

    // Programs
    programs: {
      eyebrow: "Programs",
      title: "Structured care for every season of life",
      subtitle:
        "Our programs are designed together with the community, grounded in respect, and delivered through a trusted network of volunteers.",
      items: [
        {
          title: "Funeral Support Program",
          desc: "Coordinated bereavement assistance for members and their immediate families, including logistics, financial contributions, and community presence.",
        },
        {
          title: "Wedding Support Program",
          desc: "Celebration support for members — from financial contribution to coordinated community presence on the day.",
        },
        {
          title: "Orphanage Assistance",
          desc: "Direct support to orphans and orphanages, with a focus on refugee-camp populations across Africa.",
        },
        {
          title: "Community Welfare",
          desc: "General welfare initiatives including food assistance, household support, and family emergencies.",
        },
      ],
    },

    // Leadership
    leadership: {
      eyebrow: "Leadership",
      title: "The hands that tend the tree",
      subtitle:
        "A dedicated team of volunteer leaders guiding the mission across continents.",
      executiveTitle: "Executive Leadership",
      secretaryTitle: "Secretariat",
      advisoryTitle: "Advisory Council",
      treasurersTitle: "Treasurers",
      roles: {
        president: "President",
        vp: "Vice President",
        vpSecretary: "Vice President / Secretary",
        secretary: "Secretary",
        eventCoordinator: "Event Coordinator",
        advisor: "Advisor",
        treasurer: "Treasurer",
      },
      chartTitle: "Organisational Chart",
      eventCoordinatorTitle: "Events",
    },

    // Membership
    membership: {
      eyebrow: "Membership",
      title: "Join a community rooted in care",
      subtitle: "Members contribute, participate, and benefit from structured community support.",
      whoTitle: "Who Can Join",
      whoBody: "Anyone 18+ committed to our values of respect, unity, and accountability is welcome to apply.",
      responsibilitiesTitle: "Member Responsibilities",
      responsibilities: [
        "Treat fellow members with respect and dignity",
        "Participate in community events and activities",
        "Contribute monthly and honor commitments",
        "Uphold the Code of Conduct",
        "Support fellow members during life events",
      ],
      contributionsTitle: "Contributions",
      registrationFee: "Registration",
      registrationAmount: "$25",
      registrationNote: "One-time, upon joining",
      monthlyFee: "Monthly",
      monthlyAmount: "$20",
      monthlyNote: "Recurring contribution",
      formTitle: "Join Now",
      formSubtitle: "Fill out this form and a coordinator will reach out.",
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        country: "Country",
        city: "City",
        reason: "Why would you like to join?",
      },
      successMessage: "Welcome! Your membership application has been received.",
    },

    // GetInvolved
    getInvolved: {
      eyebrow: "Get Involved",
      title: "Grow with us",
      subtitle: "Three ways to plant your support in our community.",
      tabs: {
        volunteer: "Volunteer",
        donate: "Donate",
        partner: "Partner With Us",
      },
      volunteer: {
        title: "Become a Volunteer",
        body: "Contribute your time and skills to support impactful initiatives — from event coordination to orphan programs.",
      },
      donate: {
        title: "Make a Donation",
        body: "Help us expand our reach and support more communities in need. Secure online giving is coming soon — please contact us today.",
      },
      partner: {
        title: "Partner With Us",
        body: "We collaborate with churches, companies, and NGOs that share our commitment to community welfare.",
      },
      fields: {
        fullName: "Full name",
        email: "Email",
        phone: "Phone",
        skills: "Skills / Experience",
        availability: "Availability",
        message: "Message",
        organization: "Organization",
        contactName: "Contact name",
        partnershipType: "Type of partnership",
      },
    },

    // Contact
    contact: {
      eyebrow: "Contact",
      title: "Let's talk",
      subtitle: "Reach out — we respond to every message.",
      email: "Email",
      phone: "Phone",
      office: "Office",
      officeValue: "United States",
      fields: {
        name: "Your name",
        email: "Email",
        phone: "Phone",
        subject: "Subject",
        message: "Your message",
      },
      successMessage: "Thank you! Your message has been received.",
    },

    // Footer
    footer: {
      tagline: "Rooted in compassion. Growing a better future.",
      quickLinks: "Quick Links",
      contactInfo: "Contact",
      newsletter: "Stay rooted",
      newsletterBody: "Monthly updates on our work — no spam, ever.",
      rights: "All rights reserved.",
      governance: "Governance & Policies",
    },
  },

  fr: {
    nav: {
      home: "Accueil",
      about: "À Propos",
      impact: "Impact",
      programs: "Programmes",
      leadership: "Direction",
      membership: "Adhésion",
      getInvolved: "S'impliquer",
      contact: "Contact",
      donate: "Faire un don",
      join: "Adhérer",
    },
    common: {
      learnMore: "En savoir plus",
      joinCommunity: "Rejoignez la communauté",
      donateNow: "Faire un don",
      volunteer: "Bénévolat",
      partner: "Devenir partenaire",
      submit: "Envoyer",
      sending: "Envoi...",
      required: "Obligatoire",
      thanks: "Merci !",
      close: "Fermer",
      readMore: "Lire la suite",
      viewPrograms: "Voir tous les programmes",
      comingSoon: "Don en ligne sécurisé bientôt disponible. Contactez-nous pour contribuer aujourd'hui.",
      chooseAmount: "Choisissez votre montant",
      customAmount: "Montant personnalisé",
      donateWithCard: "Faire un don sécurisé par carte",
      donating: "Préparation du paiement...",
      donationSuccess: "Merci pour votre généreux don !",
      donationCancelled: "Don annulé. Aucun débit n'a été effectué.",
      checkingPayment: "Confirmation du paiement...",
      donorNameOptional: "Votre nom (facultatif)",
      donorEmailOptional: "Votre e-mail (facultatif)",
      backHome: "Retour à l'accueil",
      tryAgain: "Réessayer",
    },
    home: {
      tagline: "Enracinés dans la compassion · Grandir ensemble",
      heroTitle1: "Autonomiser les communautés.",
      heroTitle2: "Restaurer la dignité.",
      heroTitle3: "Bâtir l'espoir.",
      heroSubtitle:
        "Soutenir les personnes et familles vulnérables aux États-Unis, en Afrique et en Europe par la compassion, l'unité et l'action.",
      impactEyebrow: "Nos domaines",
      impactTitle: "Là où nous sommes présents",
      impactStrip: [
        { title: "Portée mondiale", desc: "Actifs sur 3 continents." },
        { title: "Aide aux orphelins", desc: "Soutien aux enfants déplacés." },
        { title: "Environnement", desc: "Semer pour l'avenir." },
        { title: "Aide communautaire", desc: "Mariages, funérailles, bien-être." },
      ],
      aboutEyebrow: "Qui nous sommes",
      aboutTitle: "Un arbre pousse fort lorsque la communauté nourrit ses racines.",
      aboutBody:
        "Grand Kasaï Nsanga Lubangu Corporation (GKNL-Inc.) est une organisation à but non lucratif fondée sur l'esprit bantou de solidarité. Nous soutenons les familles vulnérables, les orphelins et les communautés à travers un accompagnement structuré.",
      programsEyebrow: "Programmes",
      programsTitle: "Comment nous aidons",
      programsItems: [
        { title: "Aide caritative", desc: "Aide directe aux personnes et familles en difficulté." },
        { title: "Aide aux orphelins", desc: "Soutien ciblé pour les orphelins, y compris en camps de réfugiés." },
        { title: "Initiatives environnementales", desc: "Plantation d'arbres et projets durables locaux." },
        { title: "Aide communautaire", desc: "Soutien aux funérailles et mariages des membres." },
      ],
      ctaEyebrow: "S'impliquer",
      ctaTitle: "Faites partie du changement",
      ctaSubtitle: "Chaque main tendue nous élève. Soyez bénévole, faites un don ou rejoignez notre communauté.",
    },
    about: {
      eyebrow: "À propos",
      title: "Ancrés dans le but. Tournés vers l'espoir.",
      whoTitle: "Qui nous sommes",
      whoBody:
        "GKNL-Inc. est une organisation à but non lucratif réunissant des personnes d'origine kasaïenne et leurs amis dans le monde entier. Nous honorons la mémoire de nos ancêtres en prenant soin des vivants.",
      missionTitle: "Notre mission",
      missionBody:
        "Soutenir les personnes et familles en difficulté financière et sociale à travers des programmes structurés de charité, de deuil, de célébration et de bien-être.",
      visionTitle: "Notre vision",
      visionBody:
        "Un réseau connecté de services à domicile, de crèches et de bénévoles offrant dignité et opportunités aux communautés vulnérables sur trois continents.",
      valuesTitle: "Valeurs fondamentales",
      values: [
        { title: "Respect & dignité", desc: "Chaque membre est traité avec honneur." },
        { title: "Unité", desc: "Nous unissons cultures, langues et générations." },
        { title: "Responsabilité", desc: "Opérations transparentes et intégrité financière." },
        { title: "Résolution pacifique", desc: "Les conflits se règlent avec sagesse et respect." },
      ],
    },
    impact: {
      eyebrow: "Notre impact",
      title: "Petits gestes, racines profondes.",
      subtitle:
        "Avec nos membres, donateurs et bénévoles, nous apportons une aide structurée là où elle compte le plus.",
      stats: [
        { value: 100, suffix: "+", label: "Familles soutenues" },
        { value: 3, suffix: "", label: "Continents" },
        { value: 12, suffix: "+", label: "Événements / an" },
        { value: 50, suffix: "+", label: "Bénévoles actifs" },
      ],
      pillars: [
        { title: "Aide humanitaire", desc: "Aide d'urgence et soutien direct aux ménages vulnérables." },
        { title: "Aide aux orphelins", desc: "Soins et fournitures pour orphelins, avec focus sur les camps de réfugiés." },
        { title: "Initiatives environnementales", desc: "Plantation d'arbres et éducation durable." },
        { title: "Aide communautaire", desc: "Soutien structuré aux funérailles, mariages et étapes familiales." },
      ],
    },
    programs: {
      eyebrow: "Programmes",
      title: "Un accompagnement structuré pour chaque étape de la vie",
      subtitle: "Nos programmes sont co-construits avec la communauté.",
      items: [
        { title: "Soutien aux funérailles", desc: "Aide au deuil coordonnée pour les membres et leurs familles." },
        { title: "Soutien aux mariages", desc: "Contribution financière et présence communautaire pour la célébration." },
        { title: "Aide aux orphelinats", desc: "Soutien aux orphelins, notamment en camps de réfugiés africains." },
        { title: "Bien-être communautaire", desc: "Aide alimentaire, soutien domestique et urgences familiales." },
      ],
    },
    leadership: {
      eyebrow: "Direction",
      title: "Celles et ceux qui prennent soin de l'arbre",
      subtitle: "Une équipe bénévole dévouée guidant la mission sur plusieurs continents.",
      executiveTitle: "Direction exécutive",
      secretaryTitle: "Secrétariat",
      advisoryTitle: "Conseil consultatif",
      treasurersTitle: "Trésoriers",
      roles: {
        president: "Président",
        vp: "Vice-président",
        vpSecretary: "Vice-président / Secrétaire",
        secretary: "Secrétaire",
        eventCoordinator: "Coordinateur des événements",
        advisor: "Conseiller",
        treasurer: "Trésorier",
      },
      chartTitle: "Organigramme",
      eventCoordinatorTitle: "Événements",
    },
    membership: {
      eyebrow: "Adhésion",
      title: "Rejoignez une communauté enracinée dans le soin",
      subtitle: "Les membres contribuent, participent et bénéficient d'un soutien communautaire structuré.",
      whoTitle: "Qui peut adhérer",
      whoBody: "Toute personne de 18 ans et plus partageant nos valeurs est la bienvenue.",
      responsibilitiesTitle: "Responsabilités du membre",
      responsibilities: [
        "Traiter les autres membres avec respect et dignité",
        "Participer aux événements communautaires",
        "Contribuer mensuellement et honorer ses engagements",
        "Respecter le code de conduite",
        "Soutenir les membres lors d'événements de vie",
      ],
      contributionsTitle: "Contributions",
      registrationFee: "Inscription",
      registrationAmount: "25 $",
      registrationNote: "Unique, à l'adhésion",
      monthlyFee: "Mensuel",
      monthlyAmount: "20 $",
      monthlyNote: "Contribution récurrente",
      formTitle: "Adhérer",
      formSubtitle: "Remplissez ce formulaire et un coordinateur vous contactera.",
      fields: {
        fullName: "Nom complet",
        email: "E-mail",
        phone: "Téléphone",
        country: "Pays",
        city: "Ville",
        reason: "Pourquoi souhaitez-vous adhérer ?",
      },
      successMessage: "Bienvenue ! Votre demande d'adhésion a été reçue.",
    },
    getInvolved: {
      eyebrow: "S'impliquer",
      title: "Grandissons ensemble",
      subtitle: "Trois façons de planter votre soutien dans notre communauté.",
      tabs: { volunteer: "Bénévolat", donate: "Don", partner: "Partenariat" },
      volunteer: {
        title: "Devenir bénévole",
        body: "Mettez votre temps et vos compétences au service d'initiatives à impact.",
      },
      donate: {
        title: "Faire un don",
        body: "Aidez-nous à étendre notre portée. Le don en ligne sécurisé arrive bientôt — contactez-nous aujourd'hui.",
      },
      partner: {
        title: "Devenir partenaire",
        body: "Nous collaborons avec des églises, entreprises et ONG qui partagent notre engagement.",
      },
      fields: {
        fullName: "Nom complet",
        email: "E-mail",
        phone: "Téléphone",
        skills: "Compétences / Expérience",
        availability: "Disponibilité",
        message: "Message",
        organization: "Organisation",
        contactName: "Contact",
        partnershipType: "Type de partenariat",
      },
    },
    contact: {
      eyebrow: "Contact",
      title: "Parlons-nous",
      subtitle: "Contactez-nous — nous répondons à chaque message.",
      email: "E-mail",
      phone: "Téléphone",
      office: "Bureau",
      officeValue: "États-Unis",
      fields: {
        name: "Votre nom",
        email: "E-mail",
        phone: "Téléphone",
        subject: "Sujet",
        message: "Votre message",
      },
      successMessage: "Merci ! Votre message a été reçu.",
    },
    footer: {
      tagline: "Enracinés dans la compassion. Construisons un avenir meilleur.",
      quickLinks: "Liens rapides",
      contactInfo: "Contact",
      newsletter: "Restez enracinés",
      newsletterBody: "Des actualités mensuelles sur notre travail — sans spam.",
      rights: "Tous droits réservés.",
      governance: "Gouvernance & politiques",
    },
  },
};

export const LOCALES = ["en", "fr"];
